import React, { useEffect, useRef, useState } from "react";
import {
    Box,
    Link,
    Separator,
    Text,
    VStack,
} from "@commonsku/design-system";
import { useDebounce } from "../../hooks";
import { oauth } from "../../utils";
import { window } from "../../global";
import { SearchBar, SearchBarStatus } from "../search-bar";
import {
    GlobalSearchResponse,
    GlobalSearchResult,
    GlobalSearchSection,
    getGlobalSearchResultHref,
    getGlobalSearchResultLabel,
} from "./globalSearch";

const MINIMUM_QUERY_LENGTH = 3;
const SEARCH_DEBOUNCE_MILLISECONDS = 400;
const TEXT_FOREGROUND = "var(--chakra-colors-text-fg, var(--chakra-colors-neutral-fg))";

const getResultKey = (result: GlobalSearchResult, index: number): string => {
    const identifier = result.purchase_order_id
        ?? result.bill_id
        ?? result.shop_number
        ?? result.client_id;

    if (identifier) {
        return String(identifier);
    }

    if (result.job_number) {
        return `${result.job_number}-${result.form_number ?? "project"}`;
    }

    return `${result.value ?? result.text ?? "result"}-${index}`;
};

const NavBarGlobalSearch = () => {
    const [value, setValue] = useState("");
    const [status, setStatus] = useState<SearchBarStatus>("idle");
    const [sections, setSections] = useState<GlobalSearchSection[]>([]);
    const requestSequence = useRef(0);

    const search = useDebounce(async (query: string, requestId: number) => {
        try {
            const { json } = await oauth(
                "GET",
                "global-search/search-project-toc",
                { value: query },
                null,
                null,
                null,
                false,
            );

            if (requestId !== requestSequence.current) {
                return;
            }

            const response = (json ?? {}) as GlobalSearchResponse;
            const results = (response.results ?? []).filter(
                (section) => 0 < section.data.length,
            );

            setSections(results);
            setStatus(0 < results.length ? "results" : "empty");
        } catch {
            if (requestId !== requestSequence.current) {
                return;
            }

            setSections([]);
            setStatus("error");
        }
    }, SEARCH_DEBOUNCE_MILLISECONDS);

    useEffect(() => () => {
        search.cancel();
        requestSequence.current += 1;
    }, [search]);

    const handleValueChange = (nextValue: string) => {
        const query = nextValue.trim();
        const requestId = requestSequence.current += 1;

        search.cancel();
        setValue(nextValue);
        setSections([]);

        if (0 === query.length) {
            setStatus("idle");
            return;
        }

        if (MINIMUM_QUERY_LENGTH > query.length) {
            setStatus("prompt");
            return;
        }

        setStatus("loading");
        search(query, requestId);
    };

    const handleSubmit = (searchValue: string) => {
        const query = searchValue.trim();

        if (0 === query.length) {
            return;
        }

        window.location.href = `/global_search.php?str=${encodeURIComponent(query)}`;
    };

    return (
        <SearchBar
            value={value}
            onValueChange={handleValueChange}
            onSubmit={handleSubmit}
            status={status}
            placeholder="Search for a project, shop, client or purchase order"
            ariaLabel="Search commonsku"
            promptMessage="Enter at least 3 characters to search."
            loadingMessage="Searching..."
            emptyMessage="No relevant results found."
            errorMessage="Search is unavailable. Please try again."
        >
            <VStack align="stretch" gap="2">
                {sections.map((section, sectionIndex) => (
                    <React.Fragment key={section.category}>
                        {0 < sectionIndex && <Separator />}
                        <VStack
                            align="flex-start"
                            alignSelf="stretch"
                            justifyContent="center"
                            gap="1.5"
                            paddingX="2"
                            paddingY="1.5"
                        >
                            <Text
                                as="h2"
                                css={{
                                    "&&&": {
                                        margin: "0",
                                    },
                                }}
                                textStyle="label"
                                color="black"
                            >
                                {section.category}
                            </Text>
                            <VStack
                                as="ul"
                                align="stretch"
                                gap="0"
                                margin="0"
                                padding="0"
                                width="full"
                                listStyleType="none"
                            >
                                {section.data.map((result, resultIndex) => {
                                    const label = getGlobalSearchResultLabel(result);

                                    return (
                                        <Box
                                            as="li"
                                            key={getResultKey(result, resultIndex)}
                                        >
                                            <Link
                                                href={getGlobalSearchResultHref(result)}
                                                display="block"
                                                paddingY="2"
                                                textStyle="sm"
                                                fontWeight="normal"
                                                color={TEXT_FOREGROUND}
                                                textDecoration="none"
                                                _hover={{
                                                    background: "bg.subtle",
                                                    color: TEXT_FOREGROUND,
                                                    textDecoration: "none",
                                                }}
                                                _focus={{
                                                    color: TEXT_FOREGROUND,
                                                }}
                                                _focusVisible={{
                                                    outline: "2px solid",
                                                    outlineColor: "brand.focusRing",
                                                    outlineOffset: "-2px",
                                                }}
                                            >
                                                <Text as="span" fontWeight="semibold">
                                                    {label.primary}
                                                </Text>
                                                {label.secondary ? ` - ${label.secondary}` : null}
                                            </Link>
                                        </Box>
                                    );
                                })}
                            </VStack>
                        </VStack>
                    </React.Fragment>
                ))}
            </VStack>
        </SearchBar>
    );
};

export default NavBarGlobalSearch;
